/**
 * RmsAuthTokenInterceptor — injeta o RMS JWT em todas as requisições HTTP.
 *
 * Adiciona `Authorization: Bearer {rmsToken}` quando:
 *   1. O RMS JWT está presente no localStorage
 *   2. A URL não é uma rota de autenticação (evita loop)
 *
 * Skips automáticos:
 *   - Rotas do próprio Auth Service (/auth/login, /auth/firebase/exchange, etc.)
 *   - APIs externas que não conhecem o RMS JWT (WordPress, Firebase, etc.)
 *   - Requisições que já possuem Authorization header
 *
 * Este interceptor é não-destrutivo: se não houver token, a requisição passa sem modificação.
 */

import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RmsAuthService } from '../services/rms-auth.service';

const PLUTUS_API_MATCHES = [
    'localhost:4000',
    'api-homol-plutus.rmscorretora.com.br',
    'api-hplutus.rmscorretora.com.br',
    'api-plutus.rmscorretora.com.br',
];

/** URLs que NÃO devem receber o header Authorization do RMS Auth Service */
const SKIP_URLS = [
    '/auth/login',
    '/auth/firebase/exchange',
    '/auth/refresh',
    'identitytoolkit.googleapis.com',
    'firebase',
    'wcup2026.org',
];

@Injectable()
export class RmsAuthTokenInterceptor implements HttpInterceptor {
    constructor(private rmsAuthService: RmsAuthService) {}

    intercept(req: HttpRequest<any>, next: HttpHandler) {
        const token = this.rmsAuthService.getStoredToken();

        if (!token) {
            return next.handle(req);
        }

        // Não injeta em rotas de auth ou externas não-RMS.
        // O Plutus usa JWT compartilhado e precisa receber o header nos hosts abaixo.
        if (this.shouldSkip(req.url) && !this.isPlutusApi(req.url)) {
            return next.handle(req);
        }

        // Não sobrescreve header Authorization já existente
        if (req.headers.has('Authorization')) {
            return next.handle(req);
        }

        const authReq = req.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
        });

        return next.handle(authReq);
    }

    private shouldSkip(url: string): boolean {
        return SKIP_URLS.some((skip) => url.includes(skip));
    }

    private isPlutusApi(url: string): boolean {
        return PLUTUS_API_MATCHES.some((match) => url.includes(match));
    }
}
